import { useMemo } from "react";

/** This is intended for use in debounce operations
 * Call set(value) with the data to be de-bounced, when the data is stable;
 * callback(stable-data) will be called.
 *
 * At any time get () can be called to see the latests text stored, this is
 * the value that either has been passed to callback or will be passed to
 * should the debounce time elapse.
 *
 * @param callback {function(string): void} a function that will
 * be called once the input has been stable for delay_ms milli-seconds.
 * @param delay_ms an optional value in ms defaults to 400 which is
 * equivalent to 30 words per minute and is likely to be slow enough
 * to debounce the typing speed of 70% of office workers.
 * @returns {{
 * set: (function(string): void)
 * clear: (function(): void),
 * get: (function(): string)}}
 */
const useDebounceCallback = (callback, delay_ms = 400) => {
  return useMemo(() => {
    const state = { value: "", counter: 0 };
    const set = (value) => {
      state.value = value;
      state.counter += 1;
      setTimeout(() => {
        state.counter -= 1;
        if (state.counter < 1) {
          state.counter = 0;
          callback(state.value);
        }
      }, delay_ms);
    };
    const get = () => state.value;
    return { set, get, clear: () => set("") };
  }, [callback, delay_ms]);
};

export default useDebounceCallback;
